import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {string | null} */
  #enterCommittedValue = null;

  /** Keep a temporarily unavailable +/- control focusable without allowing another edit.
   * @param {number} stepMultiplier
   */
  updateQuantity(stepMultiplier) {
    if (this.closest('cart-items-component')?.hasAttribute('aria-busy')) return;
    super.updateQuantity(stepMultiplier);
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('input', this.#onInput);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('input', this.#onInput);
  }

  /** @param {Event} event */
  #onInput = (event) => {
    if (event.target !== this.refs.quantityInput) return;
    this.#enterCommittedValue = null;
    // Enter ends an edit without blurring. A later keystroke starts a new edit
    // that must survive an unrelated section render while this field is focused.
    this.refs.quantityInput.setAttribute('data-skip-value-update', 'true');
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const { quantityInput } = this.refs;
    if (event.key !== 'Enter' || event.target !== quantityInput) return;

    // Cancel implicit Checkout even when Enter belongs to an IME composition.
    event.preventDefault();
    if (event.isComposing || event.keyCode === 229 || event.repeat ||
      quantityInput.disabled || this.dataset.canUpdateQuantity === 'false') return;

    // Do not silently truncate fractions, replace an empty field, or clamp an
    // invalid typed amount on Enter. Native feedback keeps the edit in place.
    if (!quantityInput.reportValidity()) return;
    if (this.#enterCommittedValue === quantityInput.value) return;

    this.setQuantity(event);
    this.#enterCommittedValue = quantityInput.value;
  };

  /** @param {Event} event */
  setQuantity(event) {
    const { quantityInput } = this.refs;
    if (event.target !== quantityInput) return;
    if (event.type === 'blur' && this.#enterCommittedValue === quantityInput.value) {
      // Pending updates disable controls and can blur this field. That blur,
      // or a later Tab, must not re-submit a quantity already committed by Enter.
      event.preventDefault();
      quantityInput.removeAttribute('data-skip-value-update');
      return;
    }
    super.setQuantity(event);
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons retain explicit server restrictions while updating numeric bounds
   */
  updateButtonStates() {
    const { minusButton, plusButton, quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Preserve Shopify line instructions as well as dynamic quantity bounds.
    const locked = this.dataset.canUpdateQuantity === 'false';
    const disableMinus = locked || value <= min;
    const disablePlus = locked || (effectiveMax !== null && value >= effectiveMax);
    const focusedButton = document.activeElement;

    // A bound can disable the initiating button before the debounced cart update
    // captures focus. Keep the shopper on this line's editable quantity instead.
    // Do not move focus for background updates or Shopify-locked quantity fields.
    if (
      !locked && !quantityInput.disabled &&
      ((focusedButton === minusButton && disableMinus) || (focusedButton === plusButton && disablePlus))
    ) {
      quantityInput.focus({ preventScroll: true });
    }

    minusButton.disabled = disableMinus;
    plusButton.disabled = disablePlus;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
